import { usePersistedState } from "@/lib/persistedFilters";
// UC CDR — voice usage rating & statements (Phase 1).
//
// Overview   · what has been ingested, busiest domains, gaps to fix
// Calls      · the normalised billable legs, filterable + CSV export
// Analytics  · where the minutes and money go: partners, destinations, carriers
// Download   · build a CDR extract by partner / domain / number / extension
// Statements · per domain + window + currency; rate a window, export its legs
// Alerts     · domains running well outside their own normal week
// Reports    · email a partner only their own usage for the window
// Rates      · WebCDR rate sheets, the rate-desk lookup, import history
// Domains    · plan / currency / rating source per domain
// Sources    · run the S3 buckets and UC Platform feeds (configured in Settings)
// Trunks     · every NAP the SBCs mention; classify it, pin a domain on it
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowDownToLine, ArrowUpFromLine, BellRing, Building2, ChartNoAxesColumn, Database, Download,
  Loader2, Mail, Receipt, RefreshCw, ScrollText, Search, Tags, TriangleAlert,
} from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Alerts from "./uccdr/Alerts";
import Analytics from "./uccdr/Analytics";
import Domains from "./uccdr/Domains";
import DownloadTab from "./uccdr/Download";
import RangeBar from "./uccdr/RangeBar";
import Rates from "./uccdr/Rates";
import Reports from "./uccdr/Reports";
import Sources from "./uccdr/Sources";
import Statements from "./uccdr/Statements";
import { DIR_TONE, Pill, Stat, clock, inZone, mins, zoneAbbrev } from "./uccdr/ui";

const TRUNK_KINDS = ["carrier", "network", "security", "e911", "unknown"];
const KIND_TONE = {
  carrier: "bg-emerald-100 text-emerald-800", network: "bg-sky-100 text-sky-800",
  security: "bg-amber-100 text-amber-800", e911: "bg-rose-100 text-rose-800",
  unknown: "bg-slate-200 text-slate-700",
};

function Overview({ data, onGoto }) {
  if (!data) return <Loader2 className="h-5 w-5 animate-spin text-slate-300" />;
  const dir = Object.fromEntries((data.by_direction || []).map((d) => [d.direction, d]));
  return (
    <div className="space-y-5" data-testid="uc-cdr-overview">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Stat label="Calls stored" value={(data.total_calls || 0).toLocaleString()}
              hint={`${(data.sources || []).length} source${(data.sources || []).length === 1 ? "" : "s"}`
                + (data.files_ingested ? ` · ${data.files_ingested} S3 files` : "")}
              testid="uc-cdr-stat-calls" />
        <Stat label="Billable legs" value={(data.billable_calls || 0).toLocaleString()}
              tone="text-emerald-700" testid="uc-cdr-stat-billable" />
        <Stat label="Inbound" value={mins(dir.inbound?.seconds)}
              hint={`${(dir.inbound?.calls || 0).toLocaleString()} calls`} testid="uc-cdr-stat-inbound" />
        <Stat label="Outbound" value={mins(dir.outbound?.seconds)}
              hint={`${(dir.outbound?.calls || 0).toLocaleString()} calls`} testid="uc-cdr-stat-outbound" />
        <Stat label="No domain matched" value={(data.unmatched_domain || 0).toLocaleString()}
              tone={data.unmatched_domain ? "text-amber-700" : "text-slate-800"}
              hint="pin a domain on the trunk" testid="uc-cdr-stat-unmatched" />
      </div>

      {!!data.by_source?.length && (
        <div className="flex flex-wrap gap-2" data-testid="uc-cdr-by-source">
          {data.by_source.map((s) => (
            <Pill key={s.source}
                  tone={s.source === "ns" ? "bg-violet-100 text-violet-800" : "bg-slate-100 text-slate-700"}>
              {s.source === "ns" ? "UC Platform" : "SBC"} · {s.calls.toLocaleString()} legs · {mins(s.seconds)}
            </Pill>
          ))}
        </div>
      )}

      {(!data.rate_rows || !!data.unrated_calls || !!data.no_rate_calls) && (
        <Card className="flex flex-wrap items-center justify-between gap-4 border-sky-200 bg-sky-50/60 px-4 py-3"
              data-testid="uc-cdr-rating-warning">
          <div className="flex items-center gap-2 text-sm text-sky-900">
            <TriangleAlert className="h-4 w-4" />
            {!data.rate_rows
              ? "No rate sheet loaded yet — import a WebCDR rate export before anything can be priced."
              : `${(data.unrated_calls || 0).toLocaleString()} legs still to price`
                + (data.no_rate_calls
                  ? `, ${data.no_rate_calls.toLocaleString()} matched no rate`
                  : "")}
          </div>
          <Button size="sm" variant="outline" data-testid="uc-cdr-goto-rating"
                  onClick={() => onGoto(data.rate_rows ? "statements" : "rates")}>
            {data.rate_rows ? "Rate this window" : "Import rates"}
          </Button>
        </Card>
      )}

      {!!data.unclassified_trunks && (
        <Card className="flex items-center justify-between gap-4 border-amber-200 bg-amber-50/60 px-4 py-3"
              data-testid="uc-cdr-trunk-warning">
          <div className="flex items-center gap-2 text-sm text-amber-900">
            <TriangleAlert className="h-4 w-4" />
            {data.unclassified_trunks} trunk{data.unclassified_trunks === 1 ? "" : "s"} still
            unclassified — traffic on them is stored but never billed.
          </div>
          <Button size="sm" variant="outline" onClick={() => onGoto("trunks")} data-testid="uc-cdr-goto-trunks">
            Classify trunks
          </Button>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="border-b border-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-700">
          Busiest domains {data.window?.label ? `· ${data.window.label}` : ""}
        </div>
        {!data.top_domains?.length ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500" data-testid="uc-cdr-top-empty">
            {data.data_from
              ? <>Nothing in this window. Stored calls run from{" "}
                  <span className="font-mono">{data.data_from}</span> to{" "}
                  <span className="font-mono">{data.data_to}</span> (UTC).</>
              : "Nothing ingested yet. Add a source, then run an ingest."}
          </div>
        ) : (
          <table className="w-full text-left text-sm" data-testid="uc-cdr-top-domains">
            <thead className="text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-1.5">Domain</th>
                <th className="px-4 py-1.5">Calls</th>
                <th className="px-4 py-1.5">Minutes</th>
                <th className="px-4 py-1.5">Split</th>
              </tr>
            </thead>
            <tbody>
              {data.top_domains.map((row) => (
                <tr key={row.domain} className="border-t border-slate-100">
                  <td className="px-4 py-1.5 font-medium text-slate-800">{row.domain}</td>
                  <td className="px-4 py-1.5 text-slate-600">{row.calls.toLocaleString()}</td>
                  <td className="px-4 py-1.5 text-slate-600">{mins(row.seconds)}</td>
                  <td className="px-4 py-1.5">
                    <div className="flex flex-wrap gap-1">
                      {(row.legs || []).map((leg) => (
                        <Pill key={leg.direction} tone={DIR_TONE[leg.direction]}>
                          {leg.direction} {mins(leg.seconds)}
                        </Pill>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function Calls({ window: win }) {
  const [filters, setFilters] = usePersistedState("uccdr.filters", { q: "", domain: "", direction: "", source: "", page: 1 });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const tz = win?.tz || "UTC";
  const params = useMemo(() => ({
    ...filters, start: win?.from_at || "", end: win?.to_at || "", tz,
  }), [filters, win?.from_at, win?.to_at, tz]);

  const load = useCallback(async () => {
    if (!win) return;
    setBusy(true);
    try {
      const { data: d } = await api.get("/uc-cdr/calls", { params: { ...params, page_size: 50 } });
      setData(d);
    } catch {
      toast.error("Could not load calls");
    } finally {
      setBusy(false);
    }
  }, [params, win]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setFilters((f) => (f.page === 1 ? f : { ...f, page: 1 })); },
    [win?.from_at, win?.to_at]);

  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const exportCsv = async () => {
    try {
      const res = await api.get("/uc-cdr/calls.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "uc-cdr.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="uc-cdr-calls">
      <div className="flex flex-wrap items-end gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="w-64 pl-8" placeholder="Number, domain or session id"
                 value={filters.q} data-testid="uc-cdr-search"
                 onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value, page: 1 }))} />
        </div>
        {["", "inbound", "outbound", "internal"].map((d) => (
          <Button key={d || "all"} size="sm" variant={filters.direction === d ? "default" : "outline"}
                  data-testid={`uc-cdr-dir-${d || "all"}`}
                  onClick={() => setFilters((f) => ({ ...f, direction: d, page: 1 }))}>
            {d || "All"}
          </Button>
        ))}
        <span className="mx-1 h-6 w-px bg-slate-200" />
        {[["", "Both"], ["ns", "UC Platform"], ["sbc", "SBC"]].map(([v, label]) => (
          <Button key={v || "any"} size="sm" variant={filters.source === v ? "default" : "outline"}
                  data-testid={`uc-cdr-src-${v || "any"}`}
                  onClick={() => setFilters((f) => ({ ...f, source: v, page: 1 }))}>
            {label}
          </Button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          {busy && <Loader2 className="h-4 w-4 animate-spin text-slate-300" />}
          <span className="text-xs text-slate-500" data-testid="uc-cdr-total">
            {(data?.total || 0).toLocaleString()} legs
          </span>
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid="uc-cdr-export">
            <Download className="h-4 w-4" /> CSV
          </Button>
        </div>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-1.5" title="the local clock you picked above">
                Start ({zoneAbbrev(tz, win?.from_at)})
              </th>
              <th className="px-3 py-1.5">Src</th>
              <th className="px-3 py-1.5">Dir</th>
              <th className="px-3 py-1.5">Domain</th>
              <th className="px-3 py-1.5">Partner</th>
              <th className="px-3 py-1.5">Ext</th>
              <th className="px-3 py-1.5">From</th>
              <th className="px-3 py-1.5">To</th>
              <th className="px-3 py-1.5">Secs</th>
              <th className="px-3 py-1.5">Carrier</th>
              <th className="px-3 py-1.5">Cause</th>
            </tr>
          </thead>
          <tbody data-testid="uc-cdr-calls-body">
            {(data?.calls || []).map((c) => (
              <tr key={c.dedup_key} className="border-t border-slate-100">
                <td className="px-3 py-1.5 font-mono text-[12px] text-slate-700"
                    title={`${c.started_at} UTC`}>{inZone(c.started_at, tz)}</td>
                <td className="px-3 py-1.5">
                  <Pill tone={c.source === "ns" ? "bg-violet-100 text-violet-800" : "bg-slate-100 text-slate-700"}>
                    {c.source === "ns" ? "UC" : "SBC"}
                  </Pill>
                </td>
                <td className="px-3 py-1.5"><Pill tone={DIR_TONE[c.direction]}>{c.direction}</Pill></td>
                <td className="px-3 py-1.5 text-slate-800">
                  {c.domain || <span className="text-amber-600">unmatched</span>}
                </td>
                <td className="px-3 py-1.5 text-slate-500">{c.territory || "—"}</td>
                <td className="px-3 py-1.5 text-slate-500">{c.extension || "—"}</td>
                <td className="px-3 py-1.5 font-mono text-[12px] text-slate-600">{c.ani || "—"}</td>
                <td className="px-3 py-1.5 font-mono text-[12px] text-slate-600">{c.dialed || "—"}</td>
                <td className="px-3 py-1.5 text-slate-700">{c.duration_seconds}</td>
                <td className="px-3 py-1.5 text-slate-600">{c.carrier || "—"}</td>
                <td className="px-3 py-1.5 text-[12px] text-slate-500">{c.termination_cause || "—"}</td>
              </tr>
            ))}
            {!data?.calls?.length && (
              <tr><td colSpan={11} className="px-3 py-8 text-center text-sm text-slate-500"
                      data-testid="uc-cdr-calls-empty">No calls match those filters.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button size="sm" variant="outline" disabled={filters.page <= 1} data-testid="uc-cdr-prev"
                  onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}>Previous</Button>
          <span className="text-xs text-slate-500">Page {filters.page} of {pages}</span>
          <Button size="sm" variant="outline" disabled={filters.page >= pages} data-testid="uc-cdr-next"
                  onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}>Next</Button>
        </div>
      )}
    </div>
  );
}

function Trunks() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState({});

  const load = useCallback(async () => {
    const { data } = await api.get("/uc-cdr/naps");
    setRows(data.naps || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (nap) => {
    try {
      await api.patch(`/uc-cdr/naps/${encodeURIComponent(nap)}`, edit[nap] || {});
      toast.success(`${nap} saved`);
      setEdit((e) => ({ ...e, [nap]: undefined }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the trunk");
    }
  };

  return (
    <div className="space-y-3" data-testid="uc-cdr-trunks">
      <p className="max-w-2xl text-sm text-slate-500">
        Every trunk (NAP) the SBCs name, discovered as files are ingested. Only
        <strong> carrier</strong> trunks are billed. Pin a domain on a trunk when its records
        arrive without one.
      </p>
      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-1.5">Trunk</th>
              <th className="px-3 py-1.5">Kind</th>
              <th className="px-3 py-1.5">Carrier</th>
              <th className="px-3 py-1.5">Pinned domain</th>
              <th className="px-3 py-1.5">Hits</th>
              <th className="px-3 py-1.5" />
            </tr>
          </thead>
          <tbody data-testid="uc-cdr-trunks-body">
            {rows.map((r) => {
              const pending = edit[r.nap];
              const row = { ...r, ...(pending || {}) };
              return (
                <tr key={r.nap} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 font-mono text-[12px] text-slate-800">{r.nap}</td>
                  <td className="px-3 py-1.5">
                    <div className="flex flex-wrap gap-1">
                      {TRUNK_KINDS.map((k) => (
                        <button key={k} type="button" data-testid={`uc-cdr-kind-${r.nap}-${k}`}
                                onClick={() => setEdit((e) => ({ ...e, [r.nap]: { ...row, kind: k } }))}
                                className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors ${row.kind === k ? KIND_TONE[k] : "bg-white text-slate-400 hover:bg-slate-100"}`}>
                          {k}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-1.5">
                    <Input className="h-7 w-36 text-[12px]" value={row.carrier || ""}
                           data-testid={`uc-cdr-carrier-${r.nap}`}
                           onChange={(e) => setEdit((s) => ({ ...s, [r.nap]: { ...row, carrier: e.target.value } }))} />
                  </td>
                  <td className="px-3 py-1.5">
                    <Input className="h-7 w-44 text-[12px]" value={row.domain || ""} placeholder="—"
                           data-testid={`uc-cdr-domain-${r.nap}`}
                           onChange={(e) => setEdit((s) => ({ ...s, [r.nap]: { ...row, domain: e.target.value } }))} />
                  </td>
                  <td className="px-3 py-1.5 text-slate-600">{(r.seen_count || 0).toLocaleString()}</td>
                  <td className="px-3 py-1.5 text-right">
                    {pending && (
                      <Button size="sm" onClick={() => save(r.nap)} data-testid={`uc-cdr-trunk-save-${r.nap}`}>
                        Save
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!rows.length && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-slate-500"
                      data-testid="uc-cdr-trunks-empty">
                No trunks discovered yet — run an ingest first.
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

const RANGE_STORE = "uc-cdr-window";

export default function UCCDR() {
  const [tab, setTab] = useState("overview");
  const [window_, setWindow] = useState(null);
  const [overview, setOverview] = useState(null);
  const [openAlerts, setOpenAlerts] = useState(0);

  useEffect(() => {
    api.get("/uc-cdr/alerts", { params: { status: "open", limit: 1 } })
      .then(({ data }) => setOpenAlerts(data.open || 0))
      .catch(() => {});
  }, []);

  // The window is resolved server-side so the browser's zone never leaks in.
  useEffect(() => {
    let saved = {};
    try { saved = JSON.parse(localStorage.getItem(RANGE_STORE) || "{}"); } catch { saved = {}; }
    const params = saved.from_local || saved.preset
      ? { start: saved.from_local || "", end: saved.to_local || "", tz: saved.tz,
          preset: saved.from_local ? "" : saved.preset }
      : { preset: "this_month" };
    api.get("/uc-cdr/window", { params })
      .then(({ data }) => setWindow(data))
      .catch(() => toast.error("Could not work out the date range"));
  }, []);

  const pickWindow = useCallback((next) => {
    setWindow(next);
    localStorage.setItem(RANGE_STORE, JSON.stringify({
      from_local: next.preset ? "" : next.from_local, to_local: next.preset ? "" : next.to_local,
      tz: next.tz, preset: next.preset || "",
    }));
  }, []);

  const loadOverview = useCallback(async () => {
    if (!window_) return;
    try {
      const { data } = await api.get("/uc-cdr/overview", {
        params: { start: window_.from_at, end: window_.to_at, tz: window_.tz },
      });
      setOverview(data);
    } catch {
      toast.error("Could not load the UC CDR overview");
    }
  }, [window_]);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  return (
    <div className="px-1" data-testid="uc-cdr-page">
      <PageHeader title="UC CDR" testid="uc-cdr-header"
                  subtitle="Voice usage from the SBC CDRs and the UC Platform — normalised, then rated per domain.">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={loadOverview} data-testid="uc-cdr-refresh">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        </div>
      </PageHeader>

      <div className="mb-4">
        <RangeBar value={window_} onChange={pickWindow} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="uc-cdr-tabs">
          <TabsTrigger value="overview" data-testid="uc-cdr-tab-overview">
            <Receipt className="mr-1.5 h-4 w-4" /> Overview
          </TabsTrigger>
          <TabsTrigger value="calls" data-testid="uc-cdr-tab-calls">
            <ArrowDownToLine className="mr-1.5 h-4 w-4" /> Calls
          </TabsTrigger>
          <TabsTrigger value="analytics" data-testid="uc-cdr-tab-analytics">
            <ChartNoAxesColumn className="mr-1.5 h-4 w-4" /> Analytics
          </TabsTrigger>
          <TabsTrigger value="download" data-testid="uc-cdr-tab-download">
            <Download className="mr-1.5 h-4 w-4" /> Download
          </TabsTrigger>
          <TabsTrigger value="statements" data-testid="uc-cdr-tab-statements">
            <ScrollText className="mr-1.5 h-4 w-4" /> Statements
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="uc-cdr-tab-alerts">
            <BellRing className="mr-1.5 h-4 w-4" /> Alerts
            {!!openAlerts && (
              <span className="ml-1.5 rounded bg-amber-100 px-1.5 text-[10px] font-semibold text-amber-800"
                    data-testid="uc-cdr-alert-badge">{openAlerts}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="uc-cdr-tab-reports">
            <Mail className="mr-1.5 h-4 w-4" /> Reports
          </TabsTrigger>
          <TabsTrigger value="rates" data-testid="uc-cdr-tab-rates">
            <Tags className="mr-1.5 h-4 w-4" /> Rates
          </TabsTrigger>
          <TabsTrigger value="domains" data-testid="uc-cdr-tab-domains">
            <Building2 className="mr-1.5 h-4 w-4" /> Domains
          </TabsTrigger>
          <TabsTrigger value="sources" data-testid="uc-cdr-tab-sources">
            <Database className="mr-1.5 h-4 w-4" /> Sources
          </TabsTrigger>
          <TabsTrigger value="trunks" data-testid="uc-cdr-tab-trunks">
            <ArrowUpFromLine className="mr-1.5 h-4 w-4" /> Trunks
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <Overview data={overview} onGoto={setTab} />
        </TabsContent>
        <TabsContent value="calls" className="mt-4"><Calls window={window_} /></TabsContent>
        <TabsContent value="analytics" className="mt-4"><Analytics window={window_} /></TabsContent>
        <TabsContent value="download" className="mt-4"><DownloadTab window={window_} /></TabsContent>
        <TabsContent value="statements" className="mt-4"><Statements window={window_} /></TabsContent>
        <TabsContent value="alerts" className="mt-4"><Alerts onCount={setOpenAlerts} /></TabsContent>
        <TabsContent value="reports" className="mt-4"><Reports window={window_} /></TabsContent>
        <TabsContent value="rates" className="mt-4"><Rates /></TabsContent>
        <TabsContent value="domains" className="mt-4"><Domains /></TabsContent>
        <TabsContent value="sources" className="mt-4"><Sources onIngested={loadOverview} /></TabsContent>
        <TabsContent value="trunks" className="mt-4"><Trunks /></TabsContent>
      </Tabs>
    </div>
  );
}
